import { FC, useEffect, useMemo } from 'react'
import {
  FuncionarioDtoEspecialidades,
  FuncionarioDtoEspecialidadesRemove,
} from '../../core/_models'
import { useFormik } from 'formik'
import { ActionType, vinculoSchema } from './validation/vinculoSchema'
import { removeEspecialidadeOfVinculo } from '../../core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'
import { OptionsHelper, showAlert, showError, ButtonType } from '_metronic/helpers'
import {
  Button,
  FetchState,
  FormRadio,
  FormSelect,
  InfoContainer,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from 'app/components'
import { FuncionarioDtoUpdate } from 'app/modules/apps/funcionarios/funcionarios-list/core/_models'
import { updateFuncionario } from 'app/modules/apps/funcionarios/funcionarios-list/core/_requests'

export const ProfissionalDeSaudeVinculoModal: FC = () => {
  const { closeModal, fetchStates } = useModal()
  const { t } = useT()

  const { data: profissional } = (fetchStates['profissional'] ||
    {}) as FetchState<FuncionarioDtoEspecialidades>

  const empresaOptions = useMemo(() => {
    return OptionsHelper.createOptions(
      profissional?.vinculos || [],
      'empresa.nomeFantasia',
      'empresa.id'
    )
  }, [profissional?.vinculos])

  const initialFormulario: {
    especialidades: IOption<number>[]
    empresa: IOption<number> | undefined
    action: ActionType
  } = {
    especialidades: [],
    empresa: undefined,
    action: ActionType.Editar,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormulario,
    validationSchema: useMemo(() => vinculoSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        switch (values.action) {
          case ActionType.Editar:
            const funcionarioId = profissional?.vinculos.find(
              (vinculo) => vinculo.empresa.id === values.empresa?.value
            )?.id
            if (funcionarioId) {
              const funcionario: FuncionarioDtoUpdate = {
                especialidades: values.especialidades.map(({ value }) => value),
              }
              await updateFuncionario(funcionarioId, funcionario)
              await showAlert({
                title: 'Sucesso',
                text: 'Vínculo editado com sucesso!',
                icon: 'success',
              })
            }
            break

          case ActionType.Remover_Especialidades:
            const removeByEspecialidades: FuncionarioDtoEspecialidadesRemove = {
              pessoa: profissional?.pessoa.id as number,
              especialidades: values.especialidades.map(({ value }) => value),
            }
            await removeEspecialidadeOfVinculo(removeByEspecialidades)
            await showAlert({
              title: 'Sucesso',
              text: 'Desvinculada especialidades!',
              icon: 'success',
            })
            break

          case ActionType.Remover_Empresa:
            const removeByEmpresa: FuncionarioDtoEspecialidadesRemove = {
              pessoa: profissional?.pessoa.id as number,
              empresa: values.empresa?.value,
            }
            await removeEspecialidadeOfVinculo(removeByEmpresa)
            await showAlert({
              title: 'Sucesso',
              text: 'Desvinculadas especialidades da instituição!',
              icon: 'success',
            })
            break

          case ActionType.Remover_All:
            const removeAll: FuncionarioDtoEspecialidadesRemove = {
              pessoa: profissional?.pessoa.id as number,
            }
            await removeEspecialidadeOfVinculo(removeAll)
            await showAlert({
              title: 'Sucesso',
              text: 'Desvinculadas todas especialidades de todos os vínnculos!',
              icon: 'success',
            })
            break
        }
        closeModal()
      } catch (error) {
        await showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { setFieldValue } = formik
  const empresaId = formik.values.empresa?.value
  const action = formik.values.action
  const vinculos = profissional?.vinculos

  useEffect(() => {
    if (empresaId) {
      const especialidades = vinculos?.find(
        (vinculo) => vinculo.empresa.id === empresaId
      )?.especialidades

      setFieldValue(
        'especialidades',
        OptionsHelper.createOptions(especialidades || [], 'nome', 'id')
      )
    } else {
      setFieldValue('especialidades', [])
    }
  }, [empresaId, vinculos, setFieldValue])

  useEffect(() => {
    setFieldValue('especialidades', [])
    setFieldValue('empresa', null)
  }, [action, setFieldValue])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {profissional?.vinculos.length === 0 && (
        <InfoContainer className='mb-3' isWarning>
          O usuário não possui vínculos ativos com {t('empresa.plural.lowercase')}.
        </InfoContainer>
      )}
      <FormRadio
        formik={formik}
        name='action'
        label='Ação'
        options={[
          {
            id: 1,
            descricao: `Editar por ${t('empresa.label')}`,
            value: ActionType.Editar,
          },
          {
            id: 2,
            descricao: 'Desvincular por Especialidades',
            value: ActionType.Remover_Especialidades,
          },
          {
            id: 3,
            descricao: `Desvincular por ${t('empresa.label')}`,
            value: ActionType.Remover_Empresa,
          },
          {
            id: 4,
            descricao: 'Desvincular por profissional de saúde',
            value: ActionType.Remover_All,
          },
        ]}
        isRequired
      />
      {(formik.values.action === ActionType.Editar ||
        formik.values.action === ActionType.Remover_Empresa) && (
        <FormSelect
          formik={formik}
          name='empresa'
          options={empresaOptions}
          className='mb-3'
          entity='empresa'
          isRequired
        />
      )}

      {formik.values.action !== ActionType.Remover_Empresa &&
        formik.values.action !== ActionType.Remover_All && (
          <FormSelect
            formik={formik}
            name='especialidades'
            fetchOptions={OptionsHelper.getOptionsEspecialidades}
            label='Especialidades'
            className='mb-3'
            placeholder='Selecione uma especialidade'
            isMulti={true}
            isRequired
          />
        )}
      <InfoContainer className='mt-3'>
        {formik.values.action === ActionType.Editar
          ? 'Edição do vínculo é específico para a instituição selecionada.'
          : formik.values.action === ActionType.Remover_Especialidades
          ? 'Desvincula as especialidades selecionadas de todos os vínculos do profissional de saúde'
          : formik.values.action === ActionType.Remover_Empresa
          ? 'Desvincula todas as especialidades do profissional de saúde da instituição selecionada.'
          : 'Desvincula todas as especialidades de todos os vínculos do profissional de saúde.'}
      </InfoContainer>

      <div className='d-flex justify-content-center mt-5'>
        <Button onClick={closeModal} className='btn-secondary me-3' disabled={formik.isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          type={ButtonType.Submit}
          className={formik.values.action === ActionType.Editar ? 'btn-primary' : 'btn-danger'}
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <LoadingIndicator />
          ) : formik.values.action === ActionType.Editar ? (
            'Editar'
          ) : (
            'Desvincular'
          )}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
